import {destroyAllSignals, selectRandom} from "p3";
import definitions from "../definitions";
import settings from "../settings";
import * as SAT from 'sat';

export default class Entity extends PIXI.Container {
    constructor() {
        super();

        this.signals = {};

        this.world = null;

        this.zSort = false;
        this.zOffset = 0;

        this.alwaysUpdate = true;
        this.destroyNextFrame = false;

        this._view = null;

        this._facingDirection = 1;

        this._collisionEnabled = false;
        this._collisionDynamic = false;
        this._collisionWith = [];
        this._collisionRadius = 0;
        this._collisionOffset = new PIXI.Point();
        this._collisionSize = new PIXI.Point();
        this._collisionDebug = null;
        this._collisionDebugAlpha = 0.4;
        this._collisionType = definitions.COLLISION_TYPE.CIRCLE;
        this._collider = null;

        this._initialized = false;
        this._enabled = true;
    }

    init() {
        this._initialized = true;

        this.initCollider(this.collisionX, this.collisionY);

        this._updateCollisionDebug();
    }

    destroy() {
        destroyAllSignals(this.signals);

        super.destroy({ children: true });
    }

    update() {
        if (this._view instanceof P3.MovieClip) {
            this._view.update(window.dt);
        }

        this._updateCollider();

        this._collisionWith.length = 0;
        this._updateCollisionDebug();
    }

    _updateCollider() {
        if(this._collider) {
            this._collider.pos.x = this.collisionX;
            this._collider.pos.y = this.collisionY;
        }
    }

    initCollider(x, y) {
        switch (this._collisionType) {
            case definitions.COLLISION_TYPE.CIRCLE:
                this._collider = new SAT.Circle(new SAT.Vector(x, y), this.collisionRadius);
                break;
            case definitions.COLLISION_TYPE.RECT:
                this._collider = new SAT.Box(new SAT.Vector(x, y), this.collisionSize.x, this.collisionSize.y).toPolygon();
                break;
        }
    }

    /**
     * @param {PIXI.Texture} texture
     */
    addViewWithTexture(texture) {
        this.removeView();

        this._view = new PIXI.Sprite(texture);
        this._view.anchor.set(0.5, 1);
        this.addChild(this._view);

        this.onViewAdded();
    }

    /**
     * @param {P3.MovieClipSequence} sequence
     * @param {string} [name="default"]
     */
    addViewWithMovieClip(sequence, name = "default") {
        this.removeView();

        this._view = new P3.MovieClip(sequence, name, false);
        this._view.anchor.set(0.5);
        this.addChild(this._view);

        this.onViewAdded();
    }

    /**
     * @param {object} spine
     */
    addViewWithSpine(spine) {
        this.removeView();

        this._view = new PIXI.spine.Spine(spine);
        this._view.skeleton.setSlotsToSetupPose();
        this._view.skeleton.setToSetupPose();
        this._view.autoUpdate = false;
        this.addChild(this._view);

        this.addSpineListener();

        this.onViewAdded();
    }

    removeView() {
        if (this._view) {
            this._view.parent.removeChild(this._view);
            this._view = null;
        }
    }

    onViewAdded() {
    }

    addSpineListener() {
        this.removeSpineListener();

        this._spineListener = {
            start: (entry) => this.onAnimationStart(entry.animation.name, entry),
            complete: (entry) => this.onAnimationComplete(entry.animation.name, entry),
            event: (entry, event) => this.onAnimationEvent(entry, event)
        };
        this._view.state.addListener(this._spineListener);
    }

    removeSpineListener() {
        if (this._spineListener !== null) {
            this._view.state.removeListener(this._spineListener);
            this._spineListener = null;
        }
    }

    /**
     * @param {Skin} skin
     */
    setSkin(skin) {
        this._view.skeleton.setSkin(skin);
        this._view.skeleton.setSlotsToSetupPose();
    }

    /**
     * @param {string|string[]} name
     * @param {boolean} [loop=false]
     * @param {boolean} [additive=false]
     * @param {Number} [track=0]
     */
    playAnimation(name, loop= false, additive= false, track= 0) {
        if (Array.isArray(name)) {
            name = selectRandom(name);
        }
        if (additive) {
            this._view.state.addAnimation(track, name, loop, 0);
        } else {
            this._view.state.setAnimation(track, name, loop);
        }
    }

    /**
     * @param {string} name
     * @param entry
     * @private
     */
    onAnimationStart(name, entry) {
    }

    /**
     * @param {string} name
     * @param entry
     * @private
     */
    onAnimationComplete(name, entry) {
    }

    /**
     * @param entry
     * @param event
     * @private
     */
    onAnimationEvent(entry, event) {
    }

    /**
     * @param {Entity} entity
     * @param {P3.Vector2} v
     */
    onCollisionEntity(entity, v) {
        this._collisionWith.push(entity);
        this._updateCollisionDebug();
    }

    /**
     * @param {Tile} tile
     * @param {P3.Vector2} v
     */
    onCollisionTile(tile, v) {
        this._collisionWith.push(tile);
        this._updateCollisionDebug();
    }

    onEnable() {
    }

    onDisable() {
    }

    /**
     * @param {SAT.Circle} entityCircle
     */
    testCollisionCircle(entityCircle) {
        return this.world.testCollisionCircle(
            this.collider,
            entityCircle
        );
        // return this.world.testCollisionCircle(
        //     this.collisionX,
        //     this.collisionY,
        //     this.collisionRadius,
        //     x,
        //     y,
        //     radius
        // );
    }

    /**
     * @param {SAT.Circle} entityCircle
     */
    testCollisionCircleFast(entityCircle) {
        return this.world.testCollisionCircleFast(
            this.collider,
            entityCircle
        );
        // return this.world.testCollisionCircleFast(
        //     this.collisionX,
        //     this.collisionY,
        //     this.collisionRadius,
        //     x,
        //     y,
        //     radius
        // );
    }

    /**
     * @param {SAT.Box} entityRect
     */
    testCollisionRect(entityRect) {
        return this.world.testCollisionRect(
            this.collider,
            entityRect
        );
        // return this.world.testCollisionRect(
        //     this.collisionX,
        //     this.collisionY,
        //     this.collisionRadius,
        //     x,
        //     y,
        //     width,
        //     height
        // );
    }

    /**
     * @param {SAT.Box} entityRect
     */
    testCollisionRectFast(entityRect) {
        return this.world.testCollisionRectFast(
            this.collider,
            entityRect
        );
        // return this.world.testCollisionRectFast(
        //     this.collisionX,
        //     this.collisionY,
        //     this.collisionRadius,
        //     x,
        //     y,
        //     width,
        //     height
        // );
    }

    testCollisionRectFastOld(x, y, width, height) {
        return this.world.testCollisionRectFastOld(
            this.collisionX,
            this.collisionY,
            this.collisionRadius,
            x,
            y,
            width,
            height
        );
    }

    /**
     * @param {Entity} entity
     * @returns {boolean}
     */
    isCollidingWith(entity) {
        return this._collisionWith.indexOf(entity) >= 0;
    }

    /**
     * @private
     */
    _updateCollisionDebug() {
        if (!this._collisionDebug && settings.WORLD_ENABLE_COLLISION_DEBUG) {
            this._collisionDebug = new PIXI.Graphics();
        } else if (this._collisionDebug && !settings.WORLD_ENABLE_COLLISION_DEBUG) {
            this._collisionDebug.destroy();
            this._collisionDebug = null;
        }

        if (this._collisionDebug) {
            this._collisionDebug.clear();

            if (this._collisionEnabled) {
                if (settings.WORLD_ENABLE_COLLISION_DEBUG) {
                    let color = this._collisionDynamic ? 0x0368FF : 0xFF0000;

                    if (this.isColliding) {
                        color = 0x00FF00;
                    }

                    switch (this._collisionType) {
                        case definitions.COLLISION_TYPE.RECT:
                            this._collisionDebug.beginFill(color, this._collisionDebugAlpha);
                            this._collisionDebug.drawRect(this._collisionOffset.x, this._collisionOffset.y, this._collisionSize.x, this._collisionSize.y);
                            this._collisionDebug.endFill();
                            break;
                        case definitions.COLLISION_TYPE.CIRCLE:
                            this._collisionDebug.beginFill(color, this._collisionDebugAlpha);
                            this._collisionDebug.drawCircle(this._collisionOffset.x, this._collisionOffset.y, this._collisionRadius);
                            this._collisionDebug.endFill();
                            break;
                        default:
                            break;
                    }

                    this.addChild(this._collisionDebug);
                }
            }
        }
    }

    /**
     * @returns {boolean}
     */
    get isInit() {
        return this._initialized;
    }

    /**
     * @returns {boolean}
     */
    get enabled() {
        return this._enabled;
    }

    /**
     * @param {boolean} value
     */
    set enabled(value) {
        this._enabled = value;
        this.renderable = this._enabled;

        if (this._enabled) {
            this.onEnable();
        } else {
            this.onDisable();
        }
    }

    /**
     * @returns {number}
     */
    get z() {
        return this.y + this.zOffset;
    }

    /**
     * @returns {PIXI.DisplayObject}
     */
    get view() {
        return this._view;
    }

    /**
     * @returns {number}
     */
    get facingDirection() {
        return this._facingDirection;
    }

    /**
     * @param {number} value
     */
    set facingDirection(value) {
        value = Math.sign(value);
        if (Math.abs(value) > 0) {
            this._facingDirection = value;
            this.scale.x = this._facingDirection;
        }
    }

    /**
     * @returns {boolean}
     */
    get collisionEnabled() {
        return this._collisionEnabled;
    }

    /**
     * @param {boolean} value
     */
    set collisionEnabled(value) {
        this._collisionEnabled = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {boolean}
     */
    get collisionDynamic() {
        return this._collisionDynamic;
    }

    /**
     * @param {boolean} value
     */
    set collisionDynamic(value) {
        this._collisionDynamic = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {boolean}
     */
    get isColliding() {
        return this._collisionWith.length > 0;
    }

    /**
     * @returns {number}
     */
    get collisionRadius() {
        return this._collisionRadius;
    }

    /**
     * @param {number} value
     */
    set collisionRadius(value) {
        this._collisionRadius = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionOffsetX() {
        return this._collisionOffset.x;
    }

    /**
     * @param {number} value
     */
    set collisionOffsetX(value) {
        this._collisionOffset.x = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionOffsetY() {
        return this._collisionOffset.y;
    }

    /**
     * @param {number} value
     */
    set collisionOffsetY(value) {
        this._collisionOffset.y = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionDebugAlpha() {
        return this._collisionDebugAlpha;
    }

    /**
     * @param {number} value
     */
    set collisionDebugAlpha(value) {
        this._collisionDebugAlpha = value;
        this._updateCollisionDebug();
    }

    /**
     * @returns {number}
     */
    get collisionX() {
        switch (this._collisionType) {
            case definitions.COLLISION_TYPE.CIRCLE: {
                return this.x + this._collisionOffset.x * this.scale.x;
            }
            case definitions.COLLISION_TYPE.RECT: {
                return this.x + this._collisionOffset.x;
            }
        }
        return this.x;
    }

    get collider() {
        return this._collider;
    }

    /**
     * @returns {number}
     */
    get collisionY() {
        switch (this._collisionType) {
            case definitions.COLLISION_TYPE.CIRCLE: {
                return this.y + this._collisionOffset.y * this.scale.y;
            }
            case definitions.COLLISION_TYPE.RECT: {
                return this.y + this._collisionOffset.y;
            }
        }
        return this.y;
    }

    /**
     * @returns {PIXI.Point}
     */
    get collisionSize() {
        return this._collisionSize;
    }

    /**
     * @param {PIXI.Point} value
     */
    set collisionSize(value) {
        this._collisionSize = value;
    }

    /**
     * @returns {number}
     */
    get collisionType() {
        return this._collisionType;
    }

    /**
     * @param {number} value
     */
    set collisionType(value) {
        this._collisionType = value;
    }
}